import React from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map, List } from 'immutable'
import { getBoundActions } from '../../../actions/index'
import {
  CONTENT_IMAGE_TYPE_HERO_NO_TEXT,
  CONTENT_IMAGE_TYPE_POSTER,
  CONTENT_IMAGE_TYPE_LOGO,
} from '../../../services/content/index'
import {
  COURSE_STATE_NOT_STARTED,
  COURSE_STATE_IN_PROGRESS,
  COURSE_STATE_COMPLETE,
  PRODUCTION_STATUS_ACTIVE,
} from '../../../services/detail/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../../Picture/index'
import DetailPageV3CourseProgressBar from './DetailPageV3CourseProgressBar'
import DetailPageV3CourseJumbotronActions from './DetailPageV3CourseJumbotronActions'
import { getOptimizedJumbotronImages, checkIfCourseWithoutProgress } from '../utils'

/**
 * @param {Object} props - The props
 * @param {Map} props.detailData - The detail data
 * @param {List} props.modulesData - The course modules data
 * @param {Map} props.progressData - The course progress data
 * @param {Map} props.featuredContent - The featured content data
 * @param {Map} props.staticText - The static text
 * @param {Object} props.ga4EventData - The GA4 event data
 * @param {function} props.onCourseStateChange - The function to change the course state
 * @returns {JSX.Element} DetailPageV3CourseJumbotron
 */

const DetailPageV3CourseJumbotron = (props) => {
  const {
    detailData = Map(),
    modulesData = List(),
    progressData = Map(),
    featuredContent = Map(),
    staticText,
    ga4EventData,
    onCourseStateChange,
  } = props

  const isCourseWithoutProgress = checkIfCourseWithoutProgress(detailData.get('contentId'))
  const courseState = isCourseWithoutProgress ? COURSE_STATE_NOT_STARTED : progressData.get('state')

  const landscapeImage = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_HERO_NO_TEXT) || Map()
  const portraitImage = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_POSTER) || Map()
  const logoImage = detailData.get('images', List()).find((i) => i.get('type') === CONTENT_IMAGE_TYPE_LOGO) || Map()

  const imageUrls = getOptimizedJumbotronImages({
    url: landscapeImage.get('url'),
    portraitUrl: portraitImage.get('url'),
  }) || {}
  const {
    fallbackImageUrl,
    smallImageUrl,
    mediumSmallImageUrl,
    mediumImageUrl,
    largeImageUrl,
    xLargeImageUrl,
  } = imageUrls

  const logoImageUrl = logoImage.get('url')
  const displayType = detailData.get('displayType')
  const showTotalProgress = detailData.get('productionStatus') !== PRODUCTION_STATUS_ACTIVE

  const contentClassName = [
    'detail-page-v3__jumbotron-content',
    displayType ? `detail-page-v3__jumbotron-content--${displayType}` : null,
    courseState === COURSE_STATE_COMPLETE ? 'detail-page-v3__jumbotron-content--completed' : null,
    courseState === COURSE_STATE_IN_PROGRESS ? 'detail-page-v3__jumbotron-content--in-progress' : null,
  ].filter((cls) => !!cls).join(' ')

  return (
    <div className="detail-page-v3__jumbotron">
      <Picture
        fallbackSrc={fallbackImageUrl}
        alt={detailData.get('title') || ''}
        imageClassName="detail-page-v3__jumbotron-image detail-page-v3__jumbotron-image--course"
        loading={IMAGE_LOADING_LAZY}
        sources={[
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: smallImageUrl,
            media: '(max-width: 480px) and (orientation: portrait)',
          },
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: mediumSmallImageUrl,
            media: '(max-width: 768px) and (orientation: portrait)',
          },
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: mediumImageUrl,
            media: '(max-width: 768px) and (orientation: landscape)',
          },
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: largeImageUrl,
            media: '(max-width: 1199px) and (orientation: landscape)',
          },
          {
            type: IMAGE_TYPE_WEBP,
            srcSet: xLargeImageUrl,
            media: '(min-width: 1200px) and (orientation: landscape)',
          },
        ]}
      />
      <div className={contentClassName}>
        <div className="detail-page-v3__layout-wrapper">
          <div className="detail-page-v3__course-jumbotron-content-container">
            <div className="detail-page-v3__course-jumbotron-content">
              {logoImageUrl && (
                <div className="detail-page-v3__course-jumbotron-logo">
                  <img src={logoImageUrl} alt={detailData.get('title') || ''} />
                </div>
              )}
              {courseState === COURSE_STATE_NOT_STARTED && (
                <div className="detail-page-v3__course-jumbotron-description">
                  {detailData.get('shortDescription')}
                </div>
              )}
              {courseState === COURSE_STATE_COMPLETE && (
                <div className="detail-page-v3__course-jumbotron-laurel">
                  {staticText.get('congratulations')}
                  <span>{staticText.get('courseCompleted')}</span>
                </div>
              )}
              <DetailPageV3CourseJumbotronActions
                detailData={detailData}
                ga4EventData={ga4EventData}
                featuredContent={featuredContent}
                progressData={progressData}
                onCourseStateChange={onCourseStateChange}
              />
            </div>
            <div className="detail-page-v3__course-jumbotron-content-container--right">
              {courseState === COURSE_STATE_IN_PROGRESS && (
                <div className="detail-page-v3__course-jumbotron-progress-bar">
                  <DetailPageV3CourseProgressBar
                    modulesData={modulesData}
                    progressData={progressData}
                    showTotalProgress={showTotalProgress}
                    hintText={staticText.get('yourProgress')}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connectRedux((state) => ({
  staticText: state.staticText.getIn(['data', 'detailPageV3', 'data'], Map()),
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(DetailPageV3CourseJumbotron)
